import { GavelOutlined, Inventory2Outlined, LockOutlined, SvgIconComponent } from '@mui/icons-material'
import moment from 'moment'
import { View } from 'react-big-calendar'
import { CaseStatus, FetchCourtDatesQuery } from '../../../../generated'
import palette from '../../../theme/palette'

export type CourtDateItem = NonNullable<FetchCourtDatesQuery['fetchCourtDates']['courtDates']>[number]

export type CourtDatesView = Extract<View, 'month' | 'week' | 'day'>

export interface CourtDateEvent {
  id: string
  title: string
  start: Date
  end: Date
  courtDate: CourtDateItem
}

export const COURT_DATES_VIEWS: CourtDatesView[] = ['month', 'week', 'day']
export const DATE_PARAM_FORMAT = 'YYYY-MM-DD'

/** A court date with no end time is shown as an hour long, matching how they are created. */
const DEFAULT_DURATION_HOURS = 1

export const isCourtDatesView = (value?: string | null): value is CourtDatesView =>
  COURT_DATES_VIEWS.includes(value as CourtDatesView)

/**
 * Court dates are wall-clock times stored as UTC (09:00Z is a 9am hearing), and the matter page reads
 * them the same way. The calendar lays events out in the browser's timezone, so rebuild each one as
 * a local Date with the same wall-clock fields; converting the instant would shift every hearing by
 * the viewer's UTC offset.
 */
export const toWallClockDate = (iso: string): Date => {
  const utc = moment.utc(iso)
  return new Date(utc.year(), utc.month(), utc.date(), utc.hours(), utc.minutes())
}

/** The whole days the current view shows, including a month view's leading/trailing weeks. */
export const getVisibleRange = (date: Date, view: CourtDatesView) => {
  const day = moment(date)
  const unit = view === 'day' ? 'day' : 'week'
  const start = view === 'month' ? day.clone().startOf('month').startOf('week') : day.clone().startOf(unit)
  const end = view === 'month' ? day.clone().endOf('month').endOf('week') : day.clone().endOf(unit)

  return { startDate: start.format(DATE_PARAM_FORMAT), endDate: end.format(DATE_PARAM_FORMAT) }
}

export const toCalendarEvent = (courtDate: CourtDateItem): CourtDateEvent => {
  const start = toWallClockDate(courtDate.hearingAt)
  const end = courtDate.hearingEndsAt
    ? toWallClockDate(courtDate.hearingEndsAt)
    : moment(start).add(DEFAULT_DURATION_HOURS, 'hours').toDate()

  return {
    id: courtDate.id,
    title: courtDate.title || 'Court Date',
    start,
    // A zero-length or inverted event collapses out of the week/day grid
    end: end > start ? end : moment(start).add(DEFAULT_DURATION_HOURS, 'hours').toDate(),
    courtDate
  }
}

export const getMatterNumber = ({ isConsultMatter, consultMatterNumber, caseNumber }: CourtDateItem) =>
  (isConsultMatter ? consultMatterNumber : caseNumber) || ''

/**
 * Matter status is shown as the icon on each court date. Labels match the rest of the portal, where a
 * completed matter reads as "Closed".
 */
export const MATTER_STATUS_STYLES: Record<CaseStatus, { label: string; Icon: SvgIconComponent }> = {
  [CaseStatus.Active]: { label: 'Active', Icon: GavelOutlined },
  [CaseStatus.Completed]: { label: 'Closed', Icon: LockOutlined },
  [CaseStatus.Archived]: { label: 'Archived', Icon: Inventory2Outlined }
}

export const getMatterStatusStyle = (status?: CaseStatus | null) =>
  MATTER_STATUS_STYLES[status || CaseStatus.Active] || MATTER_STATUS_STYLES[CaseStatus.Active]

export type CourtDateTiming = 'upcoming' | 'today' | 'past'

/**
 * Court dates still to come (today's included) are filled in the portal's primary colour; past ones
 * are only outlined in grey and faded, so what is behind us recedes. Grey matches "Expired" on the
 * matter page's court dates list.
 */
export const COURT_DATE_TIMING_STYLES: Record<CourtDateTiming, { label: string; color: string; filled: boolean }> = {
  upcoming: { label: 'Upcoming', color: palette.primary.main, filled: true },
  today: { label: 'Today', color: palette.primary.main, filled: true },
  past: { label: 'Past', color: palette.lightGrayFive, filled: false }
}

export const PAST_COURT_DATE_OPACITY = 0.6

/** A hearing that has ended is past; one still to come (or under way) on today's date is today's. */
export const getCourtDateTiming = (
  { start, end }: Pick<CourtDateEvent, 'start' | 'end'>,
  now = new Date()
): CourtDateTiming => {
  if (end < now) return 'past'
  return moment(start).isSame(now, 'day') ? 'today' : 'upcoming'
}

/**
 * How many court dates a time slot shows in the week and day views before the rest collapse into a
 * "+N more" block. Busy mornings (twenty-odd 9:00 AM hearings) were otherwise squeezed into slivers.
 */
export const VISIBLE_PER_SLOT: Record<Exclude<CourtDatesView, 'month'>, number> = { week: 2, day: 3 }

/**
 * Slot length per view. The week view uses whole hours and draws each court date only inside its own
 * hour (see {@link getWeekDisplayEnd}), so an all-day hearing cannot widen every row below it.
 */
export const SLOT_MINUTES: Record<Exclude<CourtDatesView, 'month'>, number> = { week: 60, day: 30 }

const slotStartOf = (date: Date, slotMinutes: number) => {
  const slotStart = moment(date).startOf('minute')
  return slotStart.minutes(Math.floor(slotStart.minutes() / slotMinutes) * slotMinutes)
}

/**
 * Where the week view draws a court date's end: no further than the end of the hour it starts in,
 * but at least half an hour so it stays readable. Display only; the detail and list keep real times.
 */
export const getWeekDisplayEnd = (event: CourtDateEvent) => {
  const slotEnd = slotStartOf(event.start, SLOT_MINUTES.week).add(SLOT_MINUTES.week, 'minutes')
  const minEnd = moment(event.start).add(30, 'minutes')
  return moment.max(moment.min(moment(event.end), slotEnd), minEnd).toDate()
}

/** The collapsed rest of a busy time slot, rendered as one "+N more" block that opens them as a list. */
export interface CourtDateSlotGroup {
  id: string
  title: string
  start: Date
  end: Date
  /** Every court date starting in the slot, the shown ones included, for the list it opens */
  slotCourtDates: CourtDateEvent[]
  hiddenCount: number
}

export type CourtDateCalendarItem = CourtDateEvent | CourtDateSlotGroup

export const isSlotGroup = (item: CourtDateCalendarItem): item is CourtDateSlotGroup => 'slotCourtDates' in item

/**
 * Groups court dates by the slot they start in and keeps the first `visible` of each busy
 * slot (those still on first), replacing the rest with a single {@link CourtDateSlotGroup}. A slot with just one extra court
 * date shows it instead of a "+1 more" that would take the same room.
 */
export const collapseBusySlots = (
  events: CourtDateEvent[],
  visible: number,
  slotMinutes: number,
  moreLabel: (hiddenCount: number) => string
): CourtDateCalendarItem[] => {
  const slots = new Map<number, CourtDateEvent[]>()
  // Within a slot, court dates that are still on are kept visible ahead of ones already over
  const now = new Date()
  const isPast = (event: CourtDateEvent) => (getCourtDateTiming(event, now) === 'past' ? 1 : 0)
  const sorted = [...events].sort(
    (a, b) => a.start.getTime() - b.start.getTime() || isPast(a) - isPast(b) || a.end.getTime() - b.end.getTime()
  )

  sorted.forEach(event => {
    const key = slotStartOf(event.start, slotMinutes).valueOf()
    slots.set(key, [...(slots.get(key) || []), event])
  })

  const items: CourtDateCalendarItem[] = []
  slots.forEach((group, key) => {
    if (group.length <= visible + 1) {
      items.push(...group)
      return
    }

    const hiddenCount = group.length - visible
    items.push(...group.slice(0, visible), {
      id: `more-${key}`,
      title: moreLabel(hiddenCount),
      // In line with the court dates it follows, not at the top of the slot
      start: group[0].start,
      end: moment(group[0].start).add(30, 'minutes').toDate(),
      slotCourtDates: group,
      hiddenCount
    })
  })

  return items
}

export const sortByStart = (events: CourtDateEvent[]) =>
  [...events].sort((a, b) => a.start.getTime() - b.start.getTime() || a.end.getTime() - b.end.getTime())
